import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../lib/useReducedMotion.js';

/**
 * GildUnveil — the fresh-piece ceremony as a DEVELOP-IN, not a wipe.
 *
 * The finished piece emerges the way a gilded photograph develops: soft, warm,
 * gold-toned and low-contrast at first, then resolving — sharpening, colouring,
 * cooling — into the crisp final image, with a single soft specular light-rake
 * crossing once and a warm bloom that peaks and settles. No confetti, no slide.
 * One short time-driven WebGL pass per plate, then it fades to reveal the real
 * <img>. Falls back to nothing (the plain image) on no-WebGL / reduced-motion.
 */
const DELAY_HOLD = 220; // ms held after the develop completes
const DEV_MS = 1700; // the develop duration
const FADE_MS = 360; // fade the canvas off to reveal the <img>

const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p,0.0,1.0); }`;
const FRAG = `
precision highp float;
uniform sampler2D uImg;
uniform vec2 uRes, uImgSize;
uniform float uT, uFade;
vec2 cover(vec2 px, vec2 res, vec2 ts){
  float ca=res.x/res.y, ta=ts.x/ts.y; vec2 uv=px/res, sc=vec2(1.0);
  if(ca>ta) sc.y=ta/ca; else sc.x=ca/ta;
  return (uv-0.5)*sc+0.5;
}
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
void main(){
  vec2 px = gl_FragCoord.xy;
  vec2 uv = cover(px, uRes, uImgSize);
  float t = clamp(uT, 0.0, 1.0);
  float dev = smoothstep(0.0, 1.0, t);

  // develop: soft -> crisp (cheap 5-tap blur whose radius shrinks)
  float rad = (1.0 - dev) * 0.010;
  vec3 c0 = texture2D(uImg, uv).rgb;
  vec3 cb = (c0
    + texture2D(uImg, uv + vec2(rad,0.0)).rgb
    + texture2D(uImg, uv + vec2(-rad,0.0)).rgb
    + texture2D(uImg, uv + vec2(0.0,rad)).rgb
    + texture2D(uImg, uv + vec2(0.0,-rad)).rgb) / 5.0;
  vec3 img = mix(cb, c0, dev);

  // grade: dark + desaturated + warm-gold early -> full colour late
  float l = dot(img, vec3(0.299,0.587,0.114));
  vec3 col = mix(vec3(l) * 0.55, img, dev);
  col = mix(col * vec3(1.18,0.96,0.66), col, dev);     // warm gold cast burns off

  // one soft specular gold rake, sweeping diagonally once
  float diag = uv.x*0.72 + uv.y*0.28;
  float pos = mix(-0.35, 1.35, t);
  float rake = exp(-pow((diag - pos)/0.085, 2.0));
  col += vec3(1.0,0.86,0.52) * rake * (0.32 + 0.42*(1.0 - dev));

  // warm gold bloom from the centre that peaks mid then settles
  vec2 ctr = uv - vec2(0.5, 0.46);
  float vig = exp(-dot(ctr,ctr) * 2.6);
  col += vec3(0.95,0.74,0.38) * vig * sin(t * 3.14159) * 0.15;

  // fine grain
  col += (hash(px + t*97.0) - 0.5) * 0.022;

  gl_FragColor = vec4(col, uFade);
}`;

function compile(gl: WebGLRenderingContext, t: number, src: string): WebGLShader | null {
  const s = gl.createShader(t);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    gl.deleteShader(s);
    return null;
  }
  return s;
}

export function GildUnveil({ src, delayMs = 120 }: { src: string; delayMs?: number }) {
  const reduced = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (reduced) {
      setShow(false);
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false });
    if (!gl) {
      setShow(false);
      return;
    }
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) {
      setShow(false);
      return;
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      setShow(false);
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const p = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(p);
    gl.vertexAttribPointer(p, 2, gl.FLOAT, false, 0, 0);

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const uRes = gl.getUniformLocation(prog, 'uRes');
    const uImgSize = gl.getUniformLocation(prog, 'uImgSize');
    const uT = gl.getUniformLocation(prog, 'uT');
    const uFade = gl.getUniformLocation(prog, 'uFade');

    let cw = 1;
    let ch = 1;
    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      cw = Math.max(1, Math.round(r.width * dpr));
      ch = Math.max(1, Math.round(r.height * dpr));
      canvas.width = cw;
      canvas.height = ch;
      gl.viewport(0, 0, cw, ch);
    };
    resize();

    let imgReady = false;
    let imgW = 1;
    let imgH = 1;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      imgW = image.naturalWidth || 1;
      imgH = image.naturalHeight || 1;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
        imgReady = true;
      } catch {
        setShow(false);
      }
    };
    image.src = src;

    let raf = 0;
    let startAt = 0; // when the develop begins (after delay + image ready)
    const render = (now: number): void => {
      raf = requestAnimationFrame(render);
      if (!imgReady) return;
      if (startAt === 0) startAt = now + delayMs;
      const e = now - startAt;
      if (e < 0) {
        // hold the soft first frame during the stagger delay
        gl.uniform2f(uRes, cw, ch);
        gl.uniform2f(uImgSize, imgW, imgH);
        gl.uniform1f(uT, 0);
        gl.uniform1f(uFade, 1);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        return;
      }
      const t = Math.min(1, e / DEV_MS);
      const fadeStart = DEV_MS + DELAY_HOLD;
      const fade = e <= fadeStart ? 1 : Math.max(0, 1 - (e - fadeStart) / FADE_MS);
      gl.uniform2f(uRes, cw, ch);
      gl.uniform2f(uImgSize, imgW, imgH);
      gl.uniform1f(uT, t);
      gl.uniform1f(uFade, fade);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (fade <= 0) {
        cancelAnimationFrame(raf);
        setShow(false);
      }
    };
    raf = requestAnimationFrame(render);
    window.addEventListener('resize', resize, { passive: true });

    const kill = window.setTimeout(() => setShow(false), delayMs + DEV_MS + DELAY_HOLD + FADE_MS + 2500);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(kill);
      window.removeEventListener('resize', resize);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [reduced, src, delayMs]);

  if (!show) return null;
  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />;
}
